import { useCallback, useEffect, useRef, useState } from "react";
import { grantAdminRole, revokeAdminRole, downloadAdminUsersExcel, type AdminUser } from "../../api/adminUsers";
import { useAdminUserList } from "../../hooks/useAdminUserList";
import type { AdminUserSortDirection, AdminUserSortField } from "../../utils/adminUserSort";
import { Shimmer } from "../Shimmer/Shimmer";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { useNotification } from "../Notification/NotificationProvider";
import { AdminUsersTable } from "../AdminUsersTable/AdminUsersTable";
import { AdminUserModal } from "../AdminUserModal/AdminUserModal";
import { FormDropdown } from "../FormDropdown/FormDropdown";
import "../RequestsSection/RequestsSection.scss";
import "../CreateRequestModal/CreateRequestModal.scss";
import "./AdminUsersSection.scss";

const EMAIL_FILTER_DEBOUNCE_MS = 1500;

type AdminUsersSortValue =
  | "alias:asc"
  | "alias:desc"
  | "createdAt:desc"
  | "createdAt:asc";

const SORT_OPTIONS: { value: AdminUsersSortValue; label: string }[] = [
  { value: "createdAt:desc", label: "Регистрация: сначала новые" },
  { value: "createdAt:asc", label: "Регистрация: сначала старые" },
  { value: "alias:asc", label: "Имя: А → Я" },
  { value: "alias:desc", label: "Имя: Я → А" },
];

const parseSortValue = (
  value: AdminUsersSortValue
): { field: AdminUserSortField; direction: AdminUserSortDirection } => {
  const [field, direction] = value.split(":") as [AdminUserSortField, AdminUserSortDirection];
  return { field, direction };
};

interface AdminUsersListQuery {
  page: number;
  pageSize: number;
  email: string;
  showAll: boolean;
}

export const AdminUsersSection = () => {
  const { showNotification } = useNotification();
  const [emailInput, setEmailInput] = useState("");
  const [listQuery, setListQuery] = useState<AdminUsersListQuery>({
    page: 0,
    pageSize: 10,
    email: "",
    showAll: false,
  });
  const [sortValue, setSortValue] = useState<AdminUsersSortValue>("createdAt:desc");
  const [actingUserId, setActingUserId] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const emailDebounceRef = useRef<number | null>(null);
  const { field: sortField, direction: sortDirection } = parseSortValue(sortValue);

  const { data, totalPages, loading, error, refetch, updateUser } = useAdminUserList({
    page: listQuery.page,
    pageSize: listQuery.pageSize,
    email: listQuery.email,
    showAll: listQuery.showAll,
  });

  const applyEmailFilter = useCallback((rawValue: string) => {
    const nextEmail = rawValue.trim();
    setListQuery((current) => {
      if (current.email === nextEmail) {
        return current;
      }

      return {
        ...current,
        email: nextEmail,
        page: 0,
      };
    });
  }, []);

  const handleEmailInputChange = useCallback(
    (value: string) => {
      setEmailInput(value);

      if (emailDebounceRef.current) {
        window.clearTimeout(emailDebounceRef.current);
      }

      emailDebounceRef.current = window.setTimeout(() => {
        emailDebounceRef.current = null;
        applyEmailFilter(value);
      }, EMAIL_FILTER_DEBOUNCE_MS);
    },
    [applyEmailFilter]
  );

  const handleEmailBlur = useCallback(() => {
    if (emailDebounceRef.current) {
      window.clearTimeout(emailDebounceRef.current);
      emailDebounceRef.current = null;
    }

    applyEmailFilter(emailInput);
  }, [applyEmailFilter, emailInput]);

  useEffect(
    () => () => {
      if (emailDebounceRef.current) {
        window.clearTimeout(emailDebounceRef.current);
      }
    },
    []
  );

  const handleGrantAdmin = useCallback(
    (userId: number) => {
      setActingUserId(userId);
      grantAdminRole(userId)
        .then(() => {
          updateUser(userId, (user) => ({
            ...user,
            roles: [...(user.roles ?? []).filter((role) => role !== "ADMIN"), "ADMIN"],
          }));
          setSelectedUser((current) =>
            current?.id === userId
              ? {
                  ...current,
                  roles: [...(current.roles ?? []).filter((role) => role !== "ADMIN"), "ADMIN"],
                }
              : current
          );
          showNotification({
            message: "Роль администратора выдана",
            variant: "success",
          });
        })
        .catch(() => undefined)
        .finally(() => {
          setActingUserId(null);
        });
    },
    [showNotification, updateUser]
  );

  const handleRevokeAdmin = useCallback(
    (userId: number) => {
      setActingUserId(userId);
      revokeAdminRole(userId)
        .then(() => {
          updateUser(userId, (user) => ({
            ...user,
            roles: (user.roles ?? []).filter((role) => role !== "ADMIN"),
          }));
          setSelectedUser((current) =>
            current?.id === userId
              ? {
                  ...current,
                  roles: (current.roles ?? []).filter((role) => role !== "ADMIN"),
                }
              : current
          );
          showNotification({
            message: "Роль администратора снята",
            variant: "success",
          });
        })
        .catch(() => undefined)
        .finally(() => {
          setActingUserId(null);
        });
    },
    [showNotification, updateUser]
  );

  const handleDownloadReport = useCallback(() => {
    setDownloadingReport(true);
    downloadAdminUsersExcel()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "users.xlsx";
        anchor.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => undefined)
      .finally(() => {
        setDownloadingReport(false);
      });
  }, [showNotification]);

  return (
    <section className="requests-section requests-section--embedded" aria-label="Пользователи">
      <div className="requests-section-header admin-users-section-header">
        <h1 className="requests-section-title">Пользователи</h1>
      </div>

      <div className="admin-users-section-toolbar">
        <div className="admin-users-section-toolbar-row">
          <label className="admin-users-section-filter">
            <span className="admin-users-section-filter-label">Поиск</span>
            <input
              type="search"
              className="admin-users-section-filter-input"
              placeholder="Email пользователя"
              value={emailInput}
              onChange={(event) => handleEmailInputChange(event.target.value)}
              onBlur={handleEmailBlur}
            />
          </label>

          <div className="admin-users-section-sort">
            <span className="admin-users-section-filter-label">Сортировка</span>
            <FormDropdown
              className="admin-users-section-sort-select"
              value={sortValue}
              options={SORT_OPTIONS}
              onChange={(value) => setSortValue(value as AdminUsersSortValue)}
            />
          </div>
        </div>

        <label
          className={`admin-users-section-toggle${listQuery.showAll ? " admin-users-section-toggle--active" : ""}`}
        >
          <input
            type="checkbox"
            checked={listQuery.showAll}
            onChange={(event) => {
              const showAll = event.target.checked;
              setListQuery((current) => ({
                ...current,
                showAll,
                page: 0,
              }));
            }}
          />
          <span>Архивные тарифы</span>
        </label>
      </div>

      <div className="admin-users-section-actions">
        <button
          type="button"
          className="admin-users-section-download-btn"
          disabled={downloadingReport}
          onClick={handleDownloadReport}
        >
          {downloadingReport ? <CircularProgress size="sm" /> : "Выгрузить реестр пользователей в xls"}
        </button>
      </div>

      {loading ? (
        <Shimmer rows={5} />
      ) : error ? (
        <div className="requests-section-error">
          <p className="requests-section-error__text">{error}</p>
          <button type="button" className="requests-section-error__retry" onClick={refetch}>
            Повторить
          </button>
        </div>
      ) : (
        <AdminUsersTable
          data={data}
          page={listQuery.page}
          pageSize={listQuery.pageSize}
          totalPages={totalPages}
          showArchived={listQuery.showAll}
          sortField={sortField}
          sortDirection={sortDirection}
          actingUserId={actingUserId}
          onPageChange={(page) => setListQuery((current) => ({ ...current, page }))}
          onPageSizeChange={(pageSize) =>
            setListQuery((current) => ({
              ...current,
              pageSize,
              page: 0,
            }))
          }
          onGrantAdmin={handleGrantAdmin}
          onRevokeAdmin={handleRevokeAdmin}
          onUserSelect={setSelectedUser}
        />
      )}

      {selectedUser ? (
        <AdminUserModal
          user={selectedUser}
          showArchived={listQuery.showAll}
          actingUserId={actingUserId}
          onClose={() => setSelectedUser(null)}
          onGrantAdmin={handleGrantAdmin}
          onRevokeAdmin={handleRevokeAdmin}
        />
      ) : null}
    </section>
  );
};
